import React, { useEffect, useState } from 'react'
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import formatDateTime from '../../components/formatDateTime';
import { IoIosSend } from "react-icons/io";
import FileUpload from '../../components/ul/FileUpload';
import { imageSizeExceededMessage, imageType, invalidImageMessage, MAX_FILE_SIZE_IMAGE } from '../../components/imageType';
import img1 from '../../Image/NLogo.svg';
import NotFound from '../../components/ul/NotFound';
import { FaChevronLeft, FaChevronRight, FaTrashAlt } from "react-icons/fa";

const CoinNotification = () => {
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [fileProcessing, setFileProcessing] = useState({
        image: false,
    });
    const [fileStatus, setFileStatus] = useState({
        image: null, // 'success', 'error', or null
    });
    const [file, setFile] = useState(null);
    const [formInfo, setFormInfo] = useState({
        title: '',
        message: '',
        url: '',
        timing: {
            starting: 1,
            ending: 24
        },
        coin: {
            starting: 1,
            ending: 100
        },
        claimScreen: 1
    });

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [limit, setLimit] = useState(5);

    const fetchData = async (page = 1, itemsPerPage = limit) => {
        setLoader(true);
        try {
            const resp = await Calling.getCoinNotificationHistory(page, itemsPerPage);
            if (resp?.status) {
                setData(resp.data);
                setTotalPages(resp.totalPages);
                setTotalRecords(resp.totalRecords);
                // Reset selection when page changes
                setSelectedUsers([]);
                setSelectAll(false);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoader(false);
        }
    }

    useEffect(() => {
        fetchData(currentPage);
    }, [currentPage])

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setLimit(newLimit);
        setCurrentPage(1); // Reset to first page when changing limit
        fetchData(1, newLimit);
    };

    const handleDeleteSelected = async (id) => {
        const content = `You are about to delete a notification. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                setLoader(true);
                let body = {};
                if (id) {
                    body.id = [id];
                } else {
                    body.id = selectedUsers;
                }

                const response = await Calling.deleteCoinNotification(body);
                if (response?.status) {
                    toast.success(response?.message || 'Notification deleted successfully!');
                    setSelectedUsers([]);
                    setSelectAll(false); // Reset "Select All"
                    fetchData(currentPage);
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error deleting users:', error);
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    const handleSelectAll = () => {
        if (!selectAll) {
            const allUserIds = data.map((user) => user._id);
            setSelectedUsers(allUserIds);
        } else {
            setSelectedUsers([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectUser = (id) => {
        if (selectedUsers.includes(id)) {
            setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
        } else {
            setSelectedUsers([...selectedUsers, id]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoader(true);
        try {
            const formData = new FormData();
            formData.append('title', formInfo?.title || '');
            formData.append('message', formInfo?.message || '');
            formData.append('url', formInfo?.url || '');

            // Add the new fields
            formData.append('startingTime', formInfo?.timing?.starting || 1);
            formData.append('endingTime', formInfo?.timing?.ending || 24);
            formData.append('startingCoin', formInfo?.coin?.starting || 1);
            formData.append('endingCoin', formInfo?.coin?.ending || 100);
            formData.append('claimScreen', formInfo?.claimScreen || 1);

            if (file) {
                formData.append('image', file);
            }

            const response = await Calling.sendCoinNotification(formData);
            if (response?.status) {
                toast.success(response?.message || 'Notification sent successfully!');
                setFormInfo({
                    title: '',
                    message: '',
                    url: '',
                    timing: {
                        starting: 1,
                        ending: 24
                    },
                    coin: {
                        starting: 1,
                        ending: 100
                    },
                    claimScreen: 1
                });
                setFile(null);
                setFileProcessing({
                    image: false,
                });
                setFileStatus({
                    image: null,
                });
                // After sending a new notification, go back to first page
                setCurrentPage(1);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            fetchData(currentPage);
            setLoader(false);
        }
    }

    const handleChangefile = (e) => {
        const selectedFile = e.target.files[0];

        setFileProcessing({ image: true }); // Start processing

        // Validate the MIME type
        if (selectedFile && !imageType.includes(selectedFile.type)) {
            toast.error(invalidImageMessage);
            setFile(null); // Reset file if invalid
            setFileProcessing({ image: false }); // Stop processing
            setFileStatus({ image: 'error' }); // Set error status
            e.target.value = '';
            return;
        } else {
            if (selectedFile?.size > MAX_FILE_SIZE_IMAGE) {
                setFile(null);
                setFileProcessing({ image: false }); // Stop processing
                setFileStatus({ image: 'error' });
                toast.error(imageSizeExceededMessage);
                e.target.value = '';
                return;
            }
            setFile(selectedFile); // Set the file if valid
            setFileStatus({ image: 'success' }); // Set success status
            setFileProcessing({ image: false }); // Stop processing
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;

        // Handle nested fields for timing, coin, and claimScreen
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormInfo(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: parseInt(value)
                }
            }));
        } else {
            setFormInfo(prev => ({
                ...prev,
                [name]: name === 'claimScreen' ? parseInt(value) : value
            }));
        }
    }

    // Validate range values
    const validateRanges = () => {
        const { timing, coin } = formInfo;

        if (timing.ending < timing.starting) {
            toast.error('Ending time must be greater than or equal to starting time');
            return false;
        }

        if (coin.ending < coin.starting) {
            toast.error('Ending coin must be greater than or equal to starting coin');
            return false;
        }

        return true;
    }

    const handleSend = async (info) => {
        const content = `You are about to Resend a notification. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            setLoader(true);

            try {
                let body = {
                    'id': info?._id
                }

                if (info?.n_icon) {
                    body.fileName = info?.n_icon || ''
                }
                const response = await Calling.ResendCoinNotification(body);
                if (response?.status) {
                    toast.success(response?.message || 'Notification sent successfully!');
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error submitting form:', error);
            } finally {
                fetchData(currentPage);
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    }

    // Create array of page numbers to display
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            // If total pages is less than max to show, display all pages
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // Always include first page, last page, current page, and one page before and after current
            const startPage = Math.max(1, currentPage - 1);
            const endPage = Math.min(totalPages, currentPage + 1);

            if (startPage > 1) {
                pageNumbers.push(1);
                if (startPage > 2) {
                    pageNumbers.push('...');
                }
            }

            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
            }

            if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                    pageNumbers.push('...');
                }
                pageNumbers.push(totalPages);
            }
        }

        return pageNumbers;
    };

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className='px-3 sm:px-4 lg:ml-[4.4rem] w-full mt-16 md:mt-20 bg-main-bg'>
                    {/* Form Section */}
                    <div className="bg-main-white w-full mt-4 shadow-lg rounded-lg overflow-hidden">
                        <div className='bg-main-blue text-white p-4 sm:p-6'>
                            <h1 className="text-xl sm:text-2xl font-bold mb-0">Coin Notification Center</h1>
                        </div>

                        <div className='p-4 sm:p-6'>
                            <h2 className="text-lg font-semibold mb-4 text-main-blue">Send New Notification</h2>
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                if (validateRanges()) {
                                    handleSubmit(e);
                                }
                            }} encType="multipart/form-data" className="space-y-4">
                                {/* Title Field */}
                                <div className="grid grid-cols-12 gap-3 items-center">
                                    <label className="col-span-12 sm:col-span-2 md:col-span-1 font-medium text-main-font" htmlFor="title">Title:</label>
                                    <div className="col-span-12 sm:col-span-10 md:col-span-11">
                                        <input
                                            type="text"
                                            name="title"
                                            value={formInfo?.title}
                                            onChange={handleFormChange}
                                            id="title"
                                            className="w-full p-2.5 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-main-blue transition-all"
                                            placeholder="Enter Title"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Message Field */}
                                <div className="grid grid-cols-12 gap-3 items-center">
                                    <label className="col-span-12 sm:col-span-2 md:col-span-1 font-medium text-main-font" htmlFor="message">Message:</label>
                                    <div className="col-span-12 sm:col-span-10 md:col-span-11">
                                        <input
                                            type="text"
                                            name="message"
                                            value={formInfo?.message}
                                            onChange={handleFormChange}
                                            id="message"
                                            className="w-full p-2.5 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-main-blue transition-all"
                                            placeholder="Enter Message"
                                        />
                                    </div>
                                </div>

                                {/* URL Field */}
                                <div className="grid grid-cols-12 gap-3 items-center">
                                    <label className="col-span-12 sm:col-span-2 md:col-span-1 font-medium text-main-font" htmlFor="url">URL:</label>
                                    <div className="col-span-12 sm:col-span-10 md:col-span-11">
                                        <input
                                            type="text"
                                            name="url"
                                            value={formInfo?.url}
                                            onChange={handleFormChange}
                                            id="url"
                                            className="w-full p-2.5 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-main-blue transition-all"
                                            placeholder="Enter URL"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Timing Range */}
                                <div className="grid grid-cols-12 gap-3 items-center">
                                    <label className="col-span-12 sm:col-span-2 md:col-span-1 font-medium text-main-font">Timing:</label>
                                    <div className="col-span-12 sm:col-span-10 md:col-span-11 grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm text-main-subfont mb-1 block">Starting (second)</label>
                                            <input
                                                type="number"
                                                name="timing.starting"
                                                value={formInfo?.timing?.starting}
                                                onChange={handleFormChange}
                                                min="1"
                                                className="w-full p-2.5 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-main-blue transition-all"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm text-main-subfont mb-1 block">Ending (second)</label>
                                            <input
                                                type="number"
                                                name="timing.ending"
                                                value={formInfo?.timing?.ending}
                                                onChange={handleFormChange}
                                                min={formInfo?.timing?.starting}
                                                className="w-full p-2.5 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-main-blue transition-all"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Coin Range */}
                                <div className="grid grid-cols-12 gap-3 items-center">
                                    <label className="col-span-12 sm:col-span-2 md:col-span-1 font-medium text-main-font">Coins:</label>
                                    <div className="col-span-12 sm:col-span-10 md:col-span-11 grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm text-main-subfont mb-1 block">Starting</label>
                                            <input
                                                type="number"
                                                name="coin.starting"
                                                value={formInfo?.coin?.starting}
                                                onChange={handleFormChange}
                                                min="1"
                                                className="w-full p-2.5 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-main-blue transition-all"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm text-main-subfont mb-1 block">Ending</label>
                                            <input
                                                type="number"
                                                name="coin.ending"
                                                value={formInfo?.coin?.ending}
                                                onChange={handleFormChange}
                                                min={formInfo?.coin?.starting}
                                                className="w-full p-2.5 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-main-blue transition-all"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Claim Screen Field */}
                                <div className="grid grid-cols-12 gap-3 items-center">
                                    <label className="col-span-12 sm:col-span-2 md:col-span-1 font-medium text-main-font" htmlFor="claimScreen">Claim Screen:</label>
                                    <div className="col-span-12 sm:col-span-10 md:col-span-11">
                                        <input
                                            type="number"
                                            name="claimScreen"
                                            value={formInfo?.claimScreen}
                                            onChange={handleFormChange}
                                            id="claimScreen"
                                            min="1"
                                            className="w-full p-2.5 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-main-blue transition-all"
                                            placeholder="Enter Claim Screen Number"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Icon Upload */}
                                <div className="grid grid-cols-12 gap-3 items-center">
                                    <label className="col-span-12 sm:col-span-2 md:col-span-1 font-medium text-main-font" htmlFor="icon">Icon:</label>
                                    <div className='relative col-span-12 sm:col-span-10 md:col-span-11'>
                                        {fileProcessing['image'] && <div className='absolute inset-0 flex items-center justify-center'><div className='loader'></div></div>}
                                        {fileStatus['image'] === 'error' && <div className='absolute inset-0 flex mt-3 sm:ml-[-20px] text-xl text-main-delete'>✘</div>}
                                        {fileStatus['image'] === 'success' && <div className='absolute inset-0 flex mt-3 sm:ml-[-20px] text-xl text-green-500'>✓</div>}
                                        <FileUpload
                                            id='image'
                                            name='image'
                                            accept='image/*'
                                            onChange={handleChangefile}
                                            multiple={false}
                                            className="file:bg-main-blue file:text-white file:border-0 file:rounded-lg file:px-4 file:py-2 file:mr-4 file:cursor-pointer hover:file:bg-blue-700 transition-all"
                                        />
                                        <p className={`text-[12px] mt-1 ${fileStatus['image'] === 'error' ? 'text-main-delete' : 'text-main-subfont'}`}>Only PNG and JPG files are allowed.</p>
                                    </div>
                                </div>

                                <div className="flex pt-2">
                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto bg-main-blue hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg transition-all flex items-center justify-center gap-2"
                                    >
                                        <IoIosSend size={18} />
                                        Send Notification
                                    </button>
                                </div>
                            </form>

                            {/* Delete Selected Button */}
                            {selectedUsers.length > 0 && (
                                <div className="mt-5">
                                    <button
                                        onClick={() => handleDeleteSelected(null)}
                                        className="bg-main-delete text-white px-4 py-2.5 rounded-lg hover:bg-red-600 transition-all flex items-center gap-2"
                                    >
                                        <FaTrashAlt size={16} />
                                        Delete Selected ({selectedUsers.length})
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="bg-main-th py-2 px-6">
                            <h2 className="text-lg font-semibold text-main-blue">Notification History</h2>
                        </div>

                        {/* Records per page */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-6 pt-4 gap-3">
                            <div className="flex items-center space-x-2">
                                <label htmlFor="limit" className="text-sm font-medium text-main-font">Show:</label>
                                <select
                                    id="limit"
                                    value={limit}
                                    onChange={handleLimitChange}
                                    className="p-1.5 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue text-sm"
                                >
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                </select>
                                <span className="text-sm text-main-subfont">entries</span>
                            </div>
                            <div className="text-sm text-main-subfont">
                                Showing {data.length > 0 ? (currentPage - 1) * limit + 1 : 0} to {Math.min(currentPage * limit, totalRecords)} of {totalRecords} entries
                            </div>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto px-4 sm:px-6 py-4">
                            <table className='w-full mt-2 min-w-[1200px] border-collapse'>
                                <thead className='bg-main-th text-main-font'>
                                    <tr>
                                        <th className="ps-4 py-3 text-left text-sm font-medium">
                                            <input
                                                type="checkbox"
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                                className="rounded border-main-border text-main-blue focus:ring-main-blue"
                                            />
                                        </th>
                                        <th className="px-3 py-3 text-left text-sm font-medium">#</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium">Icon</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium">Title</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium">Message</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium">Timing</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium">Coins</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium">Claim Screen</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium">URL</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium">Date</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.length > 0 ? data.map((record, index) => (
                                        <tr key={record._id} className="hover:bg-main-thHover border-b border-main-border">
                                            <td className="ps-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(record._id)}
                                                    onChange={() => handleSelectUser(record._id)}
                                                    className="rounded border-main-border text-main-blue focus:ring-main-blue"
                                                />
                                            </td>
                                            <td className="px-3 py-3">{(currentPage - 1) * limit + index + 1}</td>
                                            <td className="px-3 py-3">
                                                <div className="bg-main-th p-1 rounded-full w-12 h-12 flex items-center justify-center">
                                                    <img src={record.image || img1} alt="icon" className="w-10 h-10 rounded-full object-cover" />
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 max-w-[150px] truncate font-medium">{record.title}</td>
                                            <td className="px-3 py-3 max-w-[150px] truncate text-main-subfont">{record.message}</td>
                                            <td className="px-3 py-3 text-main-subfont">
                                                {record.timing ?
                                                    `${record.timing.starting} - ${record.timing.ending}s` :
                                                    'N/A'}
                                            </td>
                                            <td className="px-3 py-3 text-main-subfont">
                                                {record.coin ?
                                                    `${record.coin.starting} - ${record.coin.ending}` :
                                                    'N/A'}
                                            </td>
                                            <td className="px-3 py-3 text-main-subfont">
                                                {record.claimScreen || 'N/A'}
                                            </td>
                                            <td className="px-3 py-3">
                                                {record.url && (
                                                    <a
                                                        href={record.n_url}
                                                        target="_blank"
                                                        rel="noreferrer noopener"
                                                        className="bg-main-blue text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 text-sm inline-block transition-all"
                                                    >
                                                        View URL
                                                    </a>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 text-main-subfont">{formatDateTime(record.createdAt)}</td>
                                            <td className="px-3 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleSend(record)}
                                                        className='bg-main-blue hover:bg-blue-700 px-3 py-2 rounded-lg text-white transition-all'
                                                        title="Resend Notification"
                                                    >
                                                        <IoIosSend size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteSelected(record._id)}
                                                        className='bg-main-delete hover:bg-red-600 px-3 py-2 rounded-lg text-white transition-all'
                                                        title="Delete Notification"
                                                    >
                                                        <FaTrashAlt size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="11" className="text-center py-8">
                                                <NotFound />
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center py-4 border-t border-main-border">
                                <div className="flex items-center space-x-1">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={`px-3 py-1.5 rounded-md flex items-center ${currentPage === 1 ? 'bg-gray-200 text-main-subfont cursor-not-allowed' : 'bg-main-th text-main-blue hover:bg-main-thHover'} transition-all`}
                                    >
                                        <FaChevronLeft size={12} />
                                    </button>

                                    {getPageNumbers().map((page, index) => (
                                        <button
                                            key={index}
                                            onClick={() => typeof page === 'number' && handlePageChange(page)}
                                            className={`px-3.5 py-1.5 rounded-md ${page === currentPage ? 'bg-main-blue text-white' : 'bg-main-th text-main-blue hover:bg-main-thHover'} transition-all`}
                                        >
                                            {page}
                                        </button>
                                    ))}

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className={`px-3 py-1.5 rounded-md flex items-center ${currentPage === totalPages ? 'bg-gray-200 text-main-subfont cursor-not-allowed' : 'bg-main-th text-main-blue hover:bg-main-thHover'} transition-all`}
                                    >
                                        <FaChevronRight size={12} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

export default CoinNotification